import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import { Linkedin } from "lucide-react";
import { Eyebrow } from "./detail-shell";
import { LevelWhisker } from "./level-whisker";
import { C, DM, levelLabel, UI } from "./ui-tokens";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface CandidateHeaderProps {
  candidateName: string;
  candidateSubtitle?: string;
  contextLabel: string;
  result: LevelAssessmentResult;
  levelSelected: boolean;
  onOpenLevel: () => void;
  /** Candidate LinkedIn URL — renders a profile link next to the name.
   *  Absent for free-text / demo runs. */
  linkedinUrl?: string | null;
}

/** Slim candidate header: identity on the left, a clickable "Nível estimado"
 *  block on the right (big level + dispersion whisker + confidence). */
export function CandidateHeader({
  candidateName,
  candidateSubtitle,
  contextLabel,
  result,
  levelSelected,
  onOpenLevel,
  linkedinUrl,
}: CandidateHeaderProps) {
  const levels = result.modelSnapshot.levels;
  // Whisker half-width: the persisted weighted σ (same distribution as the
  // mean). Legacy rows (field absent) fall back to averaging section std-devs.
  const stddevs = [result.cross.stddev, result.vertical.stddev].filter(
    (s): s is number => s !== null,
  );
  const legacyStddev = stddevs.length ? stddevs.reduce((a, b) => a + b, 0) / stddevs.length : null;
  const overallStddev = result.overallStddev !== undefined ? result.overallStddev : legacyStddev;

  return (
    <section
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        display: "flex",
        alignItems: "stretch",
        flexWrap: "wrap",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: "1 1 300px",
          padding: "20px 24px",
          display: "flex",
          gap: 16,
          alignItems: "center",
          minWidth: 280,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            flex: "none",
            borderRadius: 13,
            background: C.ink,
            color: C.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...DM,
            fontWeight: 700,
            fontSize: 19,
          }}
        >
          {initials(candidateName)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1
              style={{
                ...DM,
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: "-0.02em",
                color: C.ink,
                margin: 0,
              }}
            >
              {candidateName}
            </h1>
            <span
              style={{
                ...UI,
                display: "inline-flex",
                alignItems: "center",
                fontWeight: 500,
                fontSize: 12,
                padding: "3px 10px",
                borderRadius: 999,
                background: C.red10,
                color: C.red,
              }}
            >
              {contextLabel}
            </span>
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Abrir perfil no LinkedIn"
                title="Abrir perfil no LinkedIn"
                data-testid="candidate-linkedin"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: C.ink50,
                  textDecoration: "none",
                }}
              >
                <Linkedin size={18} />
              </a>
            )}
          </div>
          {candidateSubtitle && (
            <div style={{ ...UI, fontSize: 13.5, color: C.ink70, marginTop: 3 }}>
              {candidateSubtitle}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenLevel}
        data-testid="overall-level-block"
        style={{
          flex: "none",
          border: "none",
          borderLeft: `1px solid ${C.borderSoft}`,
          cursor: "pointer",
          textAlign: "left",
          padding: "18px 26px",
          background: levelSelected ? C.red5 : "transparent",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 250,
          transition: "background 150ms",
        }}
      >
        <Eyebrow>Nível estimado</Eyebrow>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
          <span
            style={{
              ...DM,
              fontWeight: 700,
              fontSize: 46,
              lineHeight: 1,
              letterSpacing: "-0.03em",
              color: result.overallLevel === null ? C.amberFg : C.ink,
            }}
            data-testid="overall-level"
          >
            {levelLabel(result.overallLevel, levels.names)}
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <LevelWhisker
            mean={result.overallMean}
            stddev={overallStddev}
            levels={levels}
            width={228}
          />
        </div>
      </button>
    </section>
  );
}
